import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Banknote,
  Building2,
  ArrowRight,
  Loader2,
  AlertTriangle,
  Info,
  Lock
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

/**
 * StripeConnectCard - Allows stylists to connect their Stripe account for payouts
 * REQUIRED to accept card/Stripe payments from clients
 */
const StripeConnectCard = ({ token, onStatusChange }) => {
  const [loading, setLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState(null);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    checkConnectStatus();
    
    // Check URL params for stripe connect callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_connect') === 'success') {
      toast.success("Stripe Connect setup completed! Verifying your account...");
      window.history.replaceState({}, '', window.location.pathname);
      // Re-check status after redirect
      setTimeout(() => checkConnectStatus(), 1000);
    } else if (urlParams.get('stripe_connect') === 'refresh') {
      toast.info("Let's continue setting up your Stripe account.");
      window.history.replaceState({}, '', window.location.pathname);
      // Automatically restart onboarding
      handleSetupPayouts();
    }
  }, [token]);

  const checkConnectStatus = async () => {
    try {
      const response = await axios.get(`${API}/stylist/stripe-connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectStatus(response.data);
      // Notify parent component of status change
      if (onStatusChange) {
        onStatusChange(response.data);
      }
    } catch (error) {
      console.error("Failed to check Stripe Connect status:", error);
      setConnectStatus({ connected: false });
      if (onStatusChange) {
        onStatusChange({ connected: false });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPayouts = async () => {
    setOnboarding(true);
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const response = await axios.post(`${API}/stylist/stripe-connect/onboard`, {
        return_url: `${currentUrl}?stripe_connect=success`,
        refresh_url: `${currentUrl}?stripe_connect=refresh`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.onboarding_url) {
        // Redirect to Stripe's onboarding
        window.location.href = response.data.onboarding_url;
      } else {
        toast.error("Failed to get onboarding URL");
      }
    } catch (error) {
      console.error("Failed to start Stripe Connect onboarding:", error);
      toast.error(error.response?.data?.detail || "Failed to start payout setup");
    } finally {
      setOnboarding(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
        </CardContent>
      </Card>
    );
  }

  const isFullySetup = connectStatus?.connected && connectStatus?.payouts_enabled && connectStatus?.details_submitted;
  const isPartialSetup = connectStatus?.connected && (!connectStatus?.payouts_enabled || !connectStatus?.details_submitted);

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="stripe-connect-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Banknote className="w-5 h-5 text-green-400" />
            Accept Card Payments & Get Paid
          </CardTitle>
          {!isFullySetup && (
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Required
            </Badge>
          )}
        </div>
        <CardDescription className="text-zinc-400">
          Connect your bank account to accept card payments and receive earnings directly
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Warning Banner for Not Connected */}
        {!isFullySetup && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 font-medium">Stripe Connect Required</p>
                <p className="text-sm text-amber-300/80 mt-1">
                  You must complete Stripe Connect setup to accept card/credit payments from clients. 
                  Without this, clients can only pay via Cash, Zelle, or CashApp.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Fully Connected State */}
        {isFullySetup && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <div className="flex-1">
                <p className="text-white font-medium">Stripe Connect Active</p>
                <p className="text-sm text-zinc-400">
                  You can accept card payments and receive earnings directly to your bank
                </p>
              </div>
              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Card Payments</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-white text-sm">Enabled</span>
                </div>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Bank Payouts</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-white text-sm">Enabled</span>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              onClick={handleSetupPayouts}
              disabled={onboarding}
              data-testid="update-payout-details-btn"
            >
              {onboarding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4 mr-2" />
              )}
              Update Payment Details
            </Button>

            <p className="text-xs text-zinc-500">
              Connected {connectStatus.created_at ? `on ${new Date(connectStatus.created_at).toLocaleDateString()}` : 'successfully'}
            </p>
          </div>
        )}

        {/* Partial Setup - Needs to complete onboarding */}
        {isPartialSetup && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <AlertCircle className="w-6 h-6 text-amber-500" />
              <div className="flex-1">
                <p className="text-white font-medium">Complete Your Setup</p>
                <p className="text-sm text-zinc-400">
                  Your Stripe account is connected but needs additional information to accept payments
                </p>
              </div>
              <Badge className="bg-amber-500/20 text-amber-400">Pending</Badge>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Details Submitted</p>
                <div className="flex items-center gap-2">
                  {connectStatus.details_submitted ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <span className="text-white text-sm">
                    {connectStatus.details_submitted ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Payouts</p>
                <div className="flex items-center gap-2">
                  {connectStatus.payouts_enabled ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <span className="text-white text-sm">
                    {connectStatus.payouts_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <Button
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
              onClick={handleSetupPayouts}
              disabled={onboarding}
              data-testid="continue-stripe-setup-btn"
            >
              {onboarding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Continue Setup
            </Button>
          </div>
        )}

        {/* Not Connected State */}
        {!connectStatus?.connected && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Connect with Stripe</p>
                <p className="text-sm text-zinc-400">
                  Required to accept credit/debit card payments
                </p>
              </div>
            </div>

            {/* How It Works Section */}
            <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
              <p className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-400" />
                How it works:
              </p>
              <ol className="space-y-2 text-sm text-zinc-400 list-decimal list-inside">
                <li>Click &quot;Set Up Stripe Connect&quot; below</li>
                <li>Enter your business &amp; bank details on Stripe&apos;s secure page</li>
                <li>Once verified, you can accept card payments immediately</li>
                <li>Earnings are deposited directly to your bank account</li>
              </ol>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-300">Accept Visa, Mastercard, Amex & more</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-300">Automatic daily/weekly payouts to your bank</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-300">Track all earnings in your dashboard</span>
              </div>
            </div>

            <Button
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium h-12"
              onClick={handleSetupPayouts}
              disabled={onboarding}
              data-testid="setup-stripe-payouts-btn"
            >
              {onboarding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Building2 className="w-4 h-4 mr-2" />
                  Set Up Stripe Connect
                </>
              )}
            </Button>

            <p className="text-xs text-zinc-500 text-center">
              You&apos;ll be redirected to Stripe to securely connect your bank account. 
              This typically takes 5-10 minutes.
            </p>

            {/* What happens without Stripe Connect */}
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-300">
                  <strong>Without Stripe Connect:</strong> Card payments are disabled. 
                  Clients can only pay via Cash, Zelle, or CashApp.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Refresh Status Button */}
        {!isFullySetup && (
          <div className="pt-2 border-t border-zinc-800">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-zinc-500 hover:text-zinc-300"
              onClick={checkConnectStatus}
              data-testid="refresh-stripe-status-btn"
            >
              <RefreshCw className="w-3 h-3 mr-2" />
              Refresh Status
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeConnectCard;
